/**
 * FeaturesPage Component
 * Dedicated features page showcasing OKR Hub capabilities
 */

import React, { useEffect } from 'react';
import { SEO } from '../../components/landing/SEO';
import { LandingNav, Footer } from '../../components/landing';
import { Section } from '../../components/landing/Section';
import { Button } from '../../components/landing/Button';
import {
  Target,
  GitBranch,
  RefreshCw,
  BarChart3,
  Users2,
  MessageSquare,
  Bell,
  FileCheck,
  Download,
  Shield,
  Lock,
  Building2,
  UserCog,
  ArrowRight,
  CheckCircle2,
  Layers
} from 'lucide-react';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FeatureCategory {
  title: string;
  subtitle: string;
  description: string;
  images: string[];
  features: Feature[];
}

const featureCategories: FeatureCategory[] = [
  {
    title: 'Goal Setting & Alignment',
    subtitle: 'Create clarity from top to bottom',
    description: 'Set objectives at every level and visualize how they connect to company strategy.',
    images: ['/images/features/alignment.png'],
    features: [
      {
        icon: <Target className="w-6 h-6" />,
        title: 'Multi-Level OKRs',
        description: 'Set company, department, team, and individual objectives with clear ownership.'
      },
      {
        icon: <GitBranch className="w-6 h-6" />,
        title: 'Visual Alignment',
        description: 'Interactive hierarchy maps show how every goal connects to company strategy.'
      },
      {
        icon: <Layers className="w-6 h-6" />,
        title: 'Flexible Key Results',
        description: 'Track percentages, numbers, currency, or milestone completions.'
      },
      {
        icon: <RefreshCw className="w-6 h-6" />,
        title: 'OKR Cycles',
        description: 'Quarterly, monthly, or custom cycles with automatic transitions.'
      }
    ]
  },
  {
    title: 'Progress Tracking',
    subtitle: 'Know where you stand',
    description: 'Real-time dashboards and structured check-ins keep everyone accountable.',
    images: ['/images/features/progress.png'],
    features: [
      {
        icon: <BarChart3 className="w-6 h-6" />,
        title: 'Real-Time Dashboards',
        description: 'Live progress with automatic rollup calculations across the organization.'
      },
      {
        icon: <FileCheck className="w-6 h-6" />,
        title: 'Structured Check-ins',
        description: 'Regular updates with accomplishments, challenges, and confidence ratings.'
      },
      {
        icon: <Bell className="w-6 h-6" />,
        title: 'Smart Reminders',
        description: 'Automated email and in-app notifications keep teams accountable.'
      },
      {
        icon: <Target className="w-6 h-6" />,
        title: 'Risk Indicators',
        description: 'Surface at-risk and stagnant OKRs before they derail.'
      }
    ]
  },
  {
    title: 'Team Collaboration',
    subtitle: 'Keep everyone in sync',
    description: 'Discuss progress, tag teammates, and stay informed with real-time notifications.',
    images: ['/images/features/team-collaboration.png'],
    features: [
      {
        icon: <MessageSquare className="w-6 h-6" />,
        title: 'Contextual Comments',
        description: 'Discuss progress and blockers right where the work happens.'
      },
      {
        icon: <Users2 className="w-6 h-6" />,
        title: '@Mentions',
        description: 'Tag teammates to notify them instantly about updates.'
      },
      {
        icon: <FileCheck className="w-6 h-6" />,
        title: 'Approval Workflows',
        description: 'Optional sign-off process for OKRs before they go active.'
      },
      {
        icon: <Bell className="w-6 h-6" />,
        title: 'Activity Notifications',
        description: 'Stay informed about comments, approvals, and key updates.'
      }
    ]
  },
  {
    title: 'Analytics & Export',
    subtitle: 'Data-driven decisions',
    description: 'Comprehensive reports and flexible exports for informed decision-making.',
    images: ['/images/features/analytics-report.png'],
    features: [
      {
        icon: <BarChart3 className="w-6 h-6" />,
        title: 'Executive Dashboard',
        description: 'Completion rates, trends, and department comparisons at a glance.'
      },
      {
        icon: <Users2 className="w-6 h-6" />,
        title: 'Engagement Metrics',
        description: 'Track adoption, check-in compliance, and collaboration activity.'
      },
      {
        icon: <Target className="w-6 h-6" />,
        title: 'OKR Health Reports',
        description: 'Progress distribution, completion predictions, and health scores.'
      },
      {
        icon: <Download className="w-6 h-6" />,
        title: 'Flexible Export',
        description: 'Download data in CSV, Excel, or PDF format with custom filters.'
      }
    ]
  }
];

const securityFeatures: Feature[] = [
  {
    icon: <Lock className="w-6 h-6" />,
    title: 'Encryption Everywhere',
    description: 'Data encrypted at rest and in transit with industry-standard protocols.'
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: 'Secure Authentication',
    description: 'Modern password hashing with Argon2 and secure session management.'
  },
  {
    icon: <FileCheck className="w-6 h-6" />,
    title: 'Complete Audit Trail',
    description: 'Every action logged for compliance and security review.'
  },
  {
    icon: <UserCog className="w-6 h-6" />,
    title: 'Role-Based Access',
    description: 'Five distinct roles from Viewer to Admin with granular permissions.'
  }
];

const roles = [
  { role: 'Account Admin', description: 'Full control over settings, users, and all OKRs' },
  { role: 'Manager', description: 'Manage team OKRs, invite users, view team reports' },
  { role: 'Employee', description: 'Create personal OKRs, submit check-ins, collaborate' },
  { role: 'Viewer', description: 'Read-only access to shared OKRs and reports' }
];

export const FeaturesPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Features | OKR Hub - Complete OKR Management Platform"
        description="Explore OKR Hub features: goal alignment, progress tracking, team collaboration, analytics, and enterprise security. Everything you need to drive organizational success."
        keywords="OKR features, goal tracking, team alignment, progress dashboards, OKR analytics, enterprise OKR software"
        ogUrl="https://hr4india.com/features"
      />

      <LandingNav />

      <main>
        {/* Hero Section */}
        <Section background="primary" padding="xl">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white text-primary-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Target className="w-4 h-4" />
              <span>OKR Hub Features</span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-ink mb-6">
              Everything You Need to<br />
              <span className="text-primary-600">Execute with Clarity</span>
            </h1>

            <p className="text-xl text-ink-muted max-w-2xl mx-auto leading-relaxed mb-8">
              A focused platform for setting, tracking, and achieving your most important goals.
              No clutter. No complexity. Just what works.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="primary" as="a" href="/#contact">
                Request a Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" as="a" href="/login">
                Sign In
              </Button>
            </div>
          </div>
        </Section>

        {/* Feature Categories */}
        {featureCategories.map((category, categoryIndex) => (
          <Section
            key={categoryIndex}
            background={categoryIndex % 2 === 0 ? 'white' : 'sand'}
            padding="xl"
          >
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${categoryIndex % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content Side */}
              <div className={categoryIndex % 2 === 1 ? 'lg:order-2' : ''}>
                <p className="text-primary-500 font-semibold mb-3 uppercase tracking-wide text-sm">
                  {category.subtitle}
                </p>
                <h2 className="font-display text-3xl md:text-4xl text-ink mb-4">
                  {category.title}
                </h2>
                <p className="text-lg text-ink-muted mb-8 leading-relaxed">
                  {category.description}
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {category.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="bg-white rounded-xl p-5 border border-stone-200 hover:border-primary-300 hover:shadow-soft-md transition-all duration-300"
                    >
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-3">
                        {feature.icon}
                      </div>
                      <h3 className="font-semibold text-ink mb-1 text-sm">{feature.title}</h3>
                      <p className="text-ink-muted text-xs leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Side */}
              <div className={`flex items-center justify-center ${categoryIndex % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="bg-white rounded-2xl border border-stone-200 shadow-soft-lg overflow-hidden p-2">
                  <img
                    src={category.images[0]}
                    alt={`${category.title} screenshot`}
                    className="w-full h-auto rounded-lg max-w-lg"
                  />
                </div>
              </div>
            </div>
          </Section>
        ))}

        {/* Multi-Tenant Section */}
        <Section background="primary" padding="xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white text-primary-700 px-3 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Building2 className="w-4 h-4" />
                <span>Multi-Organization Support</span>
              </div>

              <h2 className="font-display text-3xl md:text-4xl text-ink mb-6">
                Built for Consultants &<br />Multi-Client Organizations
              </h2>

              <p className="text-lg text-ink-muted mb-8 leading-relaxed">
                Manage multiple organizations from a single platform. Each client gets
                their own isolated workspace with separate users, settings, and complete data separation.
              </p>

              <div className="space-y-4">
                {[
                  'Separate workspace per organization',
                  'Complete data isolation between clients',
                  'Independent user management',
                  'Custom branding options'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-success-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-success-600" />
                    </div>
                    <span className="text-ink">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-stone-200">
              <h3 className="font-semibold text-ink mb-6">Role-Based Access</h3>
              <div className="space-y-4">
                {roles.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 pb-4 border-b border-stone-100 last:border-0 last:pb-0">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <UserCog className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-ink">{item.role}</h4>
                      <p className="text-sm text-ink-muted">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Security Section */}
        <Section background="white" padding="xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="flex items-center justify-center">
              <div className="bg-white rounded-2xl border border-stone-200 shadow-soft-lg overflow-hidden p-2">
                <img
                  src="/images/features/security.png"
                  alt="Security features screenshot"
                  className="w-full h-auto rounded-lg max-w-lg"
                />
              </div>
            </div>

            {/* Content Side */}
            <div>
              <p className="text-primary-500 font-semibold mb-3 uppercase tracking-wide text-sm">
                Enterprise-Grade Protection
              </p>
              <h2 className="font-display text-3xl md:text-4xl text-ink mb-4">
                Security You Can Trust
              </h2>
              <p className="text-lg text-ink-muted mb-8 leading-relaxed">
                Your data is protected at every layer with encryption, access controls, and complete audit trails.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {securityFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-stone-50 rounded-xl p-5 border border-stone-200 hover:border-primary-300 hover:bg-white hover:shadow-soft-md transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-3">
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-ink mb-1 text-sm">{feature.title}</h3>
                    <p className="text-ink-muted text-xs leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <p className="text-ink-muted text-sm">
                  <span className="font-semibold">Compliance Ready:</span> Built with DPDPA 2023 and IT Act 2000 requirements in mind
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* CTA Section */}
        <Section background="sand" padding="xl">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl text-ink mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-ink-muted mb-8 leading-relaxed">
              See how OKR Hub can help your organization achieve clarity and drive results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="primary" as="a" href="/#contact">
                Request a Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" as="a" href="/">
                Back to Home
              </Button>
            </div>
          </div>
        </Section>
      </main>

      <Footer />
    </div>
  );
};
